import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import dayjs from "dayjs";

const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
  warning: "#b26a00",
  warningSoft: "#fff4e5",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const chipTones = {
  success: { bg: ui.successSoft, color: ui.success },
  danger: { bg: ui.dangerSoft, color: ui.danger },
  warning: { bg: ui.warningSoft, color: ui.warning },
  info: { bg: ui.primarySoft, color: ui.primaryDark },
  neutral: { bg: "#eef1f4", color: "#4b5563" },
};

const PillChip = ({ label, tone = "neutral" }) => {
  const t = chipTones[tone] || chipTones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 700,
        borderRadius: "8px",
        backgroundColor: t.bg,
        color: t.color,
      }}
    />
  );
};

const EmptyState = ({ icon: Icon = AssignmentOutlinedIcon, title, message }) => (
  <Box sx={{ textAlign: "center", py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: "auto",
        mb: 2,
        borderRadius: "50%",
        backgroundColor: ui.primarySoft,
        color: ui.primary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ui.text }}>
      {title}
    </Typography>
    {message && (
      <Typography
        variant="body2"
        sx={{ color: ui.muted, mt: 0.5, maxWidth: 360, mx: "auto" }}
      >
        {message}
      </Typography>
    )}
  </Box>
);

const ListSkeleton = ({ rows = 4, height = 120 }) => (
  <Box
    role="status"
    aria-label="Loading"
    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
  >
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={height} animation="wave" />
    ))}
  </Box>
);

const getStatusColor = (status) => {
  switch (status) {
    case "Done":
      return "success";
    case "In Progress":
      return "primary";
    case "To Do":
      return "default";
    default:
      return "default";
  }
};

const statusTone = (status) =>
  ({ success: "success", primary: "info" }[getStatusColor(status)] || "neutral");

const statusAccent = (status) =>
  ({ success: ui.success, primary: ui.primary }[getStatusColor(status)] ||
    "#cbd5e1");

const TaskList = ({
  isTasksLoading,
  page,
  tasks,
  tasksError,
  hasMore,
  isMobile,
  lastTaskElementRef,
  handleCompleteTask,
  handleEditTask,
}) => {
  if (isTasksLoading && page === 0) {
    return <ListSkeleton rows={3} height={132} />;
  }

  if (tasksError) {
    return (
      <Alert
        severity="error"
        variant="filled"
        sx={{
          borderRadius: "10px",
          width: "100%",
          fontSize: { xs: "0.85rem", sm: "0.95rem" },
        }}
      >
        {tasksError}
      </Alert>
    );
  }

  return (
    <>
      {Array.isArray(tasks) && tasks.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {tasks
            .sort((a, b) => {
              const aIsToDo = a.status === "To Do";
              const bIsToDo = b.status === "To Do";
              if (aIsToDo && !bIsToDo) return -1;
              if (!aIsToDo && bIsToDo) return 1;
              return dayjs(b.createdDate).unix() - dayjs(a.createdDate).unix();
            })
            .map((task, index) => {
              const isLastElement = tasks.length === index + 1;
              const isDone = task.status === "Done";
              return (
                <Box
                  key={task.id}
                  ref={isLastElement ? lastTaskElementRef : null}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: "14px",
                    border: `1px solid ${ui.border}`,
                    borderLeft: `5px solid ${statusAccent(task.status)}`,
                    backgroundColor: isDone ? ui.surface : "#fff",
                    width: "100%",
                    boxSizing: "border-box",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(16, 24, 40, 0.1)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <PillChip
                        label={task.status}
                        tone={statusTone(task.status)}
                      />
                      <PillChip
                        label={`${
                          task.actualPoints || task.storyPoints || 0
                        } pts`}
                        tone="info"
                      />
                    </Box>
                    {task.status !== "Done" && (
                      <Tooltip title="Mark as complete">
                        <IconButton
                          aria-label="complete task"
                          onClick={() => handleCompleteTask(task.id)}
                          sx={{
                            flexShrink: 0,
                            color: ui.success,
                            backgroundColor: ui.successSoft,
                            "&:hover": { backgroundColor: "#c8e6c9" },
                          }}
                          size={isMobile ? "small" : "medium"}
                        >
                          <CheckCircleOutlineIcon
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      textDecoration: isDone ? "line-through" : "none",
                      color: isDone ? ui.muted : ui.text,
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                      lineHeight: 1.4,
                      wordBreak: "break-word",
                    }}
                  >
                    {task.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: ui.muted,
                      wordBreak: "break-all",
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    {task.id}
                  </Typography>

                  <Divider sx={{ my: 1.75 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Link
                        href={task.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: ui.primary,
                          fontWeight: 600,
                          fontSize: { xs: "0.85rem", sm: "0.9rem" },
                          "&:hover": { color: ui.primaryDark },
                        }}
                      >
                        View in Jira
                        <OpenInNewIcon sx={{ fontSize: "1rem", ml: 0.5 }} />
                      </Link>
                      <Button
                        startIcon={<EditIcon fontSize="small" />}
                        size="small"
                        onClick={() => handleEditTask(task)}
                        sx={{
                          color: ui.muted,
                          textTransform: "none",
                          fontWeight: 600,
                          minWidth: 0,
                          p: 0.5,
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: ui.primaryDark,
                          },
                        }}
                      >
                        Edit
                      </Button>
                    </Box>
                    {task.dueDate && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          color: ui.muted,
                        }}
                      >
                        <ScheduleOutlinedIcon sx={{ fontSize: "1rem" }} />
                        <Typography
                          variant="caption"
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
                        >
                          Due: {dayjs(task.dueDate).format("MMM D, YYYY | HH:mm")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>
      ) : (
        <EmptyState
          title="No tasks found"
          message="No tasks found for this period."
        />
      )}

      {/* Loading More Tasks */}
      {isTasksLoading && page > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 3,
            py: 1,
          }}
        >
          <CircularProgress size={22} sx={{ color: ui.primary }} />
          <Typography variant="body2" sx={{ ml: 2, color: ui.muted }}>
            Loading more tasks...
          </Typography>
        </Box>
      )}
      {/* Scroll Indicators */}
      {!isTasksLoading && hasMore && tasks.length > 0 && (
        <Typography
          variant="body2"
          sx={{
            mt: 2.5,
            textAlign: "center",
            color: ui.primary,
            fontWeight: 500,
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
          }}
        >
          Scroll down to load more tasks
        </Typography>
      )}
      {!isTasksLoading && !hasMore && tasks.length > 0 && (
        <Typography
          variant="body2"
          sx={{
            mt: 2.5,
            textAlign: "center",
            color: ui.muted,
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
          }}
        >
          No more tasks to load
        </Typography>
      )}
    </>
  );
};

export default TaskList;